import { useMediaQuery } from 'react-responsive';
import { useEffect } from 'react';
import { useLocalStorage } from '@utils/utils.tsx';

/** What the user has chosen. `auto` means "follow the viewport". */
export type SidebarMode = 'auto' | 'expanded' | 'collapsed';
/** What the sidebar actually is right now, after resolving `auto` against the viewport. */
export type SidebarSize = 'expanded' | 'collapsed';

export const SIDEBAR_MODE_KEY = 'Layout.menuMode';
/** Pre-rework preference: true = pinned open, false = hover-collapsing. Read once, then migrated. */
const LEGACY_LOCKED_KEY = 'Layout.lockedMenu';

export const SIDEBAR_WIDTH_EXPANDED = 270;
export const SIDEBAR_WIDTH_COLLAPSED = 80;

/** Below this the sidebar is replaced by the mobile drawer + bottom bar. */
export const MOBILE_MAX_WIDTH = 1024;
/** At or above this, `auto` resolves to the full-width menu; below it, to the icon rail. */
export const SIDEBAR_EXPAND_MIN_WIDTH = 1440;

const readLegacyMode = (): SidebarMode | null => {
  try {
    const raw = window.localStorage.getItem(LEGACY_LOCKED_KEY);
    if (raw === null) return null;
    return JSON.parse(raw) === false ? 'collapsed' : 'expanded';
  } catch (e) {
    return null;
  }
};

/**
 * Size preference for the left menu.
 *
 * Three states rather than a boolean: `expanded` (the default) and `collapsed` are explicit choices
 * that survive any viewport, while `auto` lets a wide screen show the full menu and a laptop show
 * the rail. Expanded sits in the document flow and pushes the content; collapsed keeps
 * an 80px rail in flow and expands as an overlay on hover, so content never reflows under the mouse.
 */
export const useSidebarState = () => {
  // Expanded by default: a new user should see the full menu and discover that it can be collapsed,
  // rather than meeting an icon rail they have to decode. (Matches the design's "DEFAULT STATE:
  // menu is FIXED" note.) All three modes are selectable directly in the header control.
  const [mode, setMode] = useLocalStorage<SidebarMode>(SIDEBAR_MODE_KEY, 'expanded');

  const isMobile = useMediaQuery({ maxWidth: MOBILE_MAX_WIDTH });
  const isWide = useMediaQuery({ minWidth: SIDEBAR_EXPAND_MIN_WIDTH });

  // One-off migration of the old boolean. useLocalStorage only reads its own key on mount, so the
  // legacy value is picked up here and written back under the new key.
  useEffect(() => {
    if (window.localStorage.getItem(SIDEBAR_MODE_KEY) !== null) return;
    const legacy = readLegacyMode();
    if (legacy) setMode(legacy);
  }, []);

  const size: SidebarSize = mode === 'auto' ? (isWide ? 'expanded' : 'collapsed') : mode;

  return {
    mode,
    setMode,
    size,
    isMobile,
    isPinned: mode !== 'auto',
  };
};

export default useSidebarState;
